import settings from '@/settings';

/**
 * Single source of truth for turning a stored media URL into one an `<img>`
 * can actually fetch.
 *
 * The API persists uploaded media as a *relative* alias URL — e.g.
 * `/api/public-media?bucket=orgs&key=…` — deliberately, so the underlying
 * bucket/prefix can be re-pointed (American Cloud → Cloudflare R2) without
 * rewriting a single stored row. The trade-off is that the dashboard is
 * served from a different host than the API, so those paths have to be
 * re-based onto the API origin before use.
 *
 * Two cases are normalized:
 *  1. Relative `/api/public-media…` / `/api/media…` paths → absolute, on `apiURL`.
 *  2. Absolute URLs already pointing at a media endpoint but on a stale origin
 *     (e.g. a `http://api.mld.test/api/public-media?…` saved by an older env)
 *     → re-based onto the current `apiURL`.
 *
 * Anything else — a CDN link, an external https URL, a data URI — passes
 * through untouched.
 *
 * @param url    The stored URL. Nullish/empty yields `''`.
 * @param apiURL API origin to resolve against. Defaults to the configured one;
 *               pass explicitly only where the origin is threaded through (see
 *               `useTenant`, which resolves theme URLs during bootstrap).
 */
export function resolveMediaUrl(url: string | null | undefined, apiURL: string = settings.apiURL): string {
    if (!url) return '';

    if (url.startsWith('/api/public-media') || url.startsWith('/api/media')) {
        return new URL(url, apiURL).toString();
    }

    let parsed: URL;
    try {
        parsed = new URL(url);
    } catch {
        // Not an absolute URL and not a media path — leave it alone.
        return url;
    }

    if (parsed.pathname.endsWith('/api/public-media') || parsed.pathname.endsWith('/api/media')) {
        return new URL(`${parsed.pathname}${parsed.search}`, apiURL).toString();
    }

    return url;
}
